import { Grid } from "@mui/material";
import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import { AnalyticsSpacing } from "consts";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_AUDIENCE_COMPARISON_GEO_CITY_PRIMARY_STATS,
  CHART_KEY_AUDIENCE_COMPARISON_GEO_COUNTRY_PRIMARY_STATS,
  CHART_KEY_AUDIENCE_COMPARISON_GEO_REGION_PRIMARY_STATS,
  CHART_KEY_AUDIENCE_COMPARISON_GEO_SESSIONS_BY_COUNTRIES_MAP,
  CHART_KEY_AUDIENCE_COMPARISON_GEO_TOP_CITIES_BY_CONVERSIONS,
  CHART_KEY_AUDIENCE_COMPARISON_GEO_TOP_CITIES_BY_SESSIONS,
  CHART_KEY_AUDIENCE_COMPARISON_GEO_TOP_COUNTRIES_BY_CONVERSIONS,
  CHART_KEY_AUDIENCE_COMPARISON_GEO_TOP_COUNTRIES_BY_SESSIONS,
  CHART_KEY_AUDIENCE_COMPARISON_GEO_TOP_REGIONS_BY_CONVERSIONS,
  CHART_KEY_AUDIENCE_COMPARISON_GEO_TOP_REGIONS_BY_SESSIONS,
} from "features/analytics/constants/chartKeys";

function AudienceGeographicTab() {
  return (
    <Grid container spacing={AnalyticsSpacing}>
      <Grid item xs={12} sx={{ height: 600 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_AUDIENCE_COMPARISON_GEO_SESSIONS_BY_COUNTRIES_MAP}
          chart={{
            name: "Total Sessions by Countries",
            dimension: [
              ApiDimension.DIMENSION_LAT_LONG,
              ApiDimension.DIMENSION_COUNTRY,
              ApiDimension.DIMENSION_CITY,
            ],
            template: ApiChartTemplate.MAP,
            type: ApiChartType.GRAPH,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
              },
            ],
            displayOptions: {
              noFilters: true,
              filterDropdowns: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
            },
          }}
        />
      </Grid>

      <Grid item xs={8} sx={{ minHeight: 830 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_AUDIENCE_COMPARISON_GEO_COUNTRY_PRIMARY_STATS}
          chart={{
            name: "Country Primary Stats by Audiences",
            dimension: [ApiDimension.DIMENSION_COUNTRY],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                  ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE,
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
              filterDropdowns: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
              rowsPerPage: 11,
            },
          }}
        />
      </Grid>
      <Grid item xs={4} container spacing={AnalyticsSpacing}>
        <Grid item xs={12}>
          <StandardAnalyticsCard
            chartKey={
              CHART_KEY_AUDIENCE_COMPARISON_GEO_TOP_COUNTRIES_BY_SESSIONS
            }
            chart={{
              name: "Top 10 Countries by Sessions",
              dimension: [ApiDimension.DIMENSION_COUNTRY],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.PIE,
              attributes: [],
              limit: 10,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                },
              ],
              displayOptions: {
                noFilters: true,
                filterDropdowns: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
                showTotals: true,
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <StandardAnalyticsCard
            chartKey={
              CHART_KEY_AUDIENCE_COMPARISON_GEO_TOP_COUNTRIES_BY_CONVERSIONS
            }
            chart={{
              name: "Top 10 Countries by Conversions",
              dimension: [ApiDimension.DIMENSION_COUNTRY],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.PIE,
              attributes: [],
              limit: 10,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT],
                },
              ],
              displayOptions: {
                noFilters: true,
                filterDropdowns: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
                showTotals: true,
              },
            }}
          />
        </Grid>
      </Grid>
      <Grid item xs={8} sx={{ minHeight: 830 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_AUDIENCE_COMPARISON_GEO_REGION_PRIMARY_STATS}
          chart={{
            name: "Region Primary Stats by Audiences",
            dimension: [ApiDimension.DIMENSION_REGION],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                  ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE,
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
              filterDropdowns: [
                ApiDimension.DIMENSION_COUNTRY,
                ApiDimension.DIMENSION_LEAD_AUDIENCE,
              ],
              rowsPerPage: 11,
            },
          }}
        />
      </Grid>
      <Grid item xs={4} container spacing={AnalyticsSpacing}>
        <Grid item xs={12}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_AUDIENCE_COMPARISON_GEO_TOP_REGIONS_BY_SESSIONS}
            chart={{
              name: "Top 10 Regions by Sessions",
              dimension: [ApiDimension.DIMENSION_REGION],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.PIE,
              attributes: [],
              limit: 10,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                },
              ],
              displayOptions: {
                noFilters: true,
                filterDropdowns: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
                showTotals: true,
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <StandardAnalyticsCard
            chartKey={
              CHART_KEY_AUDIENCE_COMPARISON_GEO_TOP_REGIONS_BY_CONVERSIONS
            }
            chart={{
              name: "Top 10 Regions by Conversions",
              dimension: [ApiDimension.DIMENSION_REGION],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.PIE,
              attributes: [],
              limit: 10,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT],
                },
              ],
              displayOptions: {
                noFilters: true,
                filterDropdowns: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
                showTotals: true,
              },
            }}
          />
        </Grid>
      </Grid>
      <Grid item xs={8} sx={{ minHeight: 830 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_AUDIENCE_COMPARISON_GEO_CITY_PRIMARY_STATS}
          chart={{
            name: "City Primary Stats by Audiences",
            dimension: [ApiDimension.DIMENSION_CITY],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                  ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE,
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
              filterDropdowns: [
                ApiDimension.DIMENSION_COUNTRY,
                ApiDimension.DIMENSION_LEAD_AUDIENCE,
              ],
              rowsPerPage: 11,
            },
          }}
        />
      </Grid>
      <Grid item xs={4} container spacing={AnalyticsSpacing}>
        <Grid item xs={12}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_AUDIENCE_COMPARISON_GEO_TOP_CITIES_BY_SESSIONS}
            chart={{
              name: "Top 10 Cities by Sessions",
              dimension: [ApiDimension.DIMENSION_CITY],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.PIE,
              attributes: [],
              limit: 10,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                },
              ],
              displayOptions: {
                noFilters: true,
                filterDropdowns: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
                showTotals: true,
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <StandardAnalyticsCard
            chartKey={
              CHART_KEY_AUDIENCE_COMPARISON_GEO_TOP_CITIES_BY_CONVERSIONS
            }
            chart={{
              name: "Top 10 Cities by Conversions",
              dimension: [ApiDimension.DIMENSION_CITY],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.PIE,
              attributes: [],
              limit: 10,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT],
                },
              ],
              displayOptions: {
                noFilters: true,
                filterDropdowns: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
                showTotals: true,
              },
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );
}

export default AudienceGeographicTab;
